import { Dialog, Transition } from "@headlessui/react";
import { CardCombobox } from "./CardCombobox";
import { useFormik } from "formik";
import React, { Fragment, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import CopyToClipboard from "react-copy-to-clipboard";
import { BsCheck2 } from "react-icons/bs";
import * as Yup from "yup";
import { FiChevronDown } from "react-icons/fi";
import { IoMdAttach, IoMdClose, IoMdInformationCircleOutline } from "react-icons/io";
import { IoCopy } from "react-icons/io5";
import toast from "react-hot-toast";

import { formatFileSize } from "../../../utils/formatFileSize";
import { compressImage } from "../../../utils/compressImage";
import { InitialFormValues, TradeGiftCard, TradeType } from "./types";
import { useCreateOrderMutation } from "../../../features/order/orderSlice";
import { TradeFormValidationSchema } from "./TradeFormValidationSchema";
import { TradeSvg } from "./TradeSvg";
import { useGetCurrenciesQuery } from "@/features/trade&coins/coinSlice";

const currenciesSelect = [
  { name: "NGN", value: "ngn", symbol: "₦" },
  { name: "GHS", value: "ghs", symbol: "GH₵" },
  { name: "USD", value: "usd", symbol: "$" },
];

const MAX_FILES = 10;

export const TradeGiftcard = ({
  cards,
  name,
  isDisabled,
  isTradeModalOpen,
  setIsTradeModalOpen,
  initialCardId,
  initialRate,
  hideButton = false,
}: TradeGiftCard) => {
  // copy to clipboard state -----------
  const [copy, setCopy] = useState<{ value: string; copied: boolean }>({
    value: "23g4js34gbsd330n0ed8rcn",
    copied: false,
  });

  const [isCurrencyInfoOpen, setIsCurrencyInfoOpen] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [currentRate, setCurrentRate] = useState(initialRate || 0);
  const [createOrder, { isLoading, isError }] = useCreateOrderMutation();

  const [currency, setCurrency] = useState("ngn");

  const { data: currencies, isLoading: isCurrencyLoading } =
    useGetCurrenciesQuery();

  const formik = useFormik<InitialFormValues>({
    initialValues: {
      type: TradeType.sell,
      card: initialCardId || "0",
      amount: 0.0,
      extrainfo: "",
      file: null,
      acceptTerms: true,
    },
    validationSchema: TradeFormValidationSchema,
    onSubmit: async (values) => {
      // Validate files count: minimum 1, maximum 10
      if (files.length < 1) {
        formik.setFieldError("file", "Please upload at least 1 image");
        toast.error("Please upload at least 1 image");
        return;
      }
      if (files.length > MAX_FILES) {
        formik.setFieldError("file", `Maximum ${MAX_FILES} images allowed`);
        toast.error(`Maximum ${MAX_FILES} images allowed`);
        return;
      }

      const t0 = Date.now();
      console.log("[order-submit] starting", {
        type: values.type,
        cardId: values.card,
        amount: values.amount,
        fileCount: files.length,
        files: files.map((f, i) => ({
          idx: i,
          name: f.name,
          size: f.size,
          type: f.type,
        })),
      });

      const loadingId = toast.loading(
        `Compressing images (0/${files.length})...`,
      );

      try {
        const compressStart = Date.now();
        let completed = 0;
        const compressed: File[] = await Promise.all(
          files.map(async (original, i) => {
            const finish = (result: File) => {
              completed += 1;
              toast.loading(
                `Compressing images (${completed}/${files.length})...`,
                { id: loadingId },
              );
              return result;
            };
            try {
              return finish(await compressImage(original));
            } catch (e: any) {
              console.log("[order-submit] compress failed, using original", {
                idx: i,
                name: original.name,
                error: e?.message,
              });
              return finish(original);
            }
          }),
        );
        const totalBefore = files.reduce((s, f) => s + f.size, 0);
        const totalAfter = compressed.reduce((s, f) => s + f.size, 0);
        console.log("[order-submit] images compressed", {
          count: compressed.length,
          totalBeforeBytes: totalBefore,
          totalAfterBytes: totalAfter,
          ratio: totalBefore
            ? Number((totalAfter / totalBefore).toFixed(3))
            : null,
          compressMs: Date.now() - compressStart,
        });

        const formData = new FormData();
        formData.append("type", values.type);
        formData.append("card", values.card);
        formData.append("quantity", values.amount.toString());
        formData.append("note", values.extrainfo);
        for (const f of compressed) formData.append("images", f);

        console.log("[order-submit] formData built, sending request", {
          elapsedMs: Date.now() - t0,
        });

        toast.loading(
          `Uploading order (${(totalAfter / 1024 / 1024).toFixed(2)} MB)...`,
          { id: loadingId },
        );

        const res: any = await createOrder(formData).unwrap();

        console.log("[order-submit] success", {
          elapsedMs: Date.now() - t0,
          orderId: res?._id,
        });

        formik.resetForm();
        setFiles([]);
        toast.dismiss(loadingId);
        toast.success("Order created successfully!");
        setIsTradeModalOpen(false);
      } catch (error: any) {
        toast.dismiss(loadingId);
        console.log("[order-submit] ERROR", {
          elapsedMs: Date.now() - t0,
          status: error?.status,
          originalStatus: error?.originalStatus,
          data: error?.data,
          message: error?.message,
          errorString: error?.error,
          raw: error,
        });

        let message = "Something went wrong. Try again";
        if (error?.data?.message) message = error.data.message;
        else if (error?.data?.error) message = error.data.error;
        else if (error?.status === "FETCH_ERROR")
          message = "Network error — please check your connection";
        else if (error?.status === "TIMEOUT_ERROR")
          message = "Request timed out — please try again";
        else if (error?.status === "PARSING_ERROR")
          message = "Unexpected server response";
        else if (error?.status === 413)
          message = "Files are too large — try fewer or smaller images";
        else if (error?.status === 504)
          message = "Server timed out — try again";
        else if (error?.status === 502 || error?.status === 503)
          message = "Server is unavailable — please try again shortly";
        else if (typeof error?.status === "number")
          message = `Server error (${error.status})`;
        else if (error?.message) message = error.message;

        toast.error(message);
      }
    },
  });

  // Temporarily lift Headless UI's body scroll lock while currency info modal is open
  // so mobile Safari allows touch scrolling inside the portal-rendered overlay
  useEffect(() => {
    if (!isCurrencyInfoOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "unset";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [isCurrencyInfoOpen]);

  const [estimate, setEstimate] = useState<number>(0);

  // Reset rate when modal opens with initial values
  useEffect(() => {
    if (initialRate && isTradeModalOpen) {
      setCurrentRate(initialRate);
    }
  }, [initialRate, isTradeModalOpen]);

  useEffect(() => {
    let estimate = 0;
    let cx = currency;

    if (!currency) cx = "ngn";

    switch (currency) {
      case "ngn":
        estimate = currentRate * Number(formik.values.amount);
        break;
      case "ghs":
        estimate =
          (currentRate * Number(formik.values.amount) || 0) /
          (currencies?.find((currency: any) => currency?.name === "ghs")
            ?.rate || 0);
        break;
      case "usd":
        estimate =
          (currentRate * Number(formik.values.amount) || 0) /
          (currencies?.find((currency: any) => currency?.name === "usd")
            ?.rate || 0);
        break;
      default:
        estimate = 0;
        break;
    }

    if (estimate == Infinity) estimate = 0;

    setEstimate(estimate);
  }, [currentRate, currencies, currency, formik.values.amount]);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileObj = e.target.files && e.target.files;
    if (!fileObj) {
      return;
    }
    const newFiles = Object.values(fileObj);
    const remainingSlots = MAX_FILES - files.length;
    
    if (remainingSlots <= 0) {
      toast.error(`Maximum ${MAX_FILES} files allowed`);
      e.target.value = ""; // Reset input
      return;
    }
    
    // Only take files up to the remaining slots
    const filesToAdd = newFiles.slice(0, remainingSlots);
    
    if (newFiles.length > remainingSlots) {
      toast.error(`Only ${remainingSlots} more file(s) can be added. ${newFiles.length - remainingSlots} file(s) were not added.`);
    }
    
    setFiles((prevFiles) => [...prevFiles, ...filesToAdd]);
    // Clear file validation error when files are added
    if (formik.errors.file) {
      formik.setFieldError("file", undefined);
    }
    e.target.value = ""; // Reset input to allow re-selecting same files
    formik.handleChange(e);
  };

  const handleResetFile = (key: string) => {
    const newFile = files.filter((item) => item.name !== key);
    setFiles(newFile);
    // Clear file validation error when files are removed
    if (formik.errors.file) {
      formik.setFieldError("file", undefined);
    }
  };

  return (
    <div>
      {!hideButton && (
        <button
          className="btn-primary px-12 py-2 mt-8 disabled:bg-zinc-500 disabled:text-zinc-400 disabled:cursor-not-allowed"
          disabled={isDisabled}
          onClick={() => setIsTradeModalOpen(true)}
        >
          Sell
        </button>
      )}

      <Transition appear show={isTradeModalOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => {
            if (!isCurrencyInfoOpen) setIsTradeModalOpen(false);
          }}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="relative font-dashboard w-full max-w-3xl min-h-[500px] transform overflow-hidden rounded-2xl bg-[#161819] px-4 pt-4 pb-10 text-left align-middle shadow-xl transition-all">
                  <div className="flex items-center justify-between px-4 pt-2 pb-4">
                    <div className="flex items-center gap-3">
                      <div className="box-content p-2.5 rounded-full bg-primary">
                        <TradeSvg />
                      </div>

                      <p className="text-base text-white/40 font-light">
                        Trade card ({name})
                      </p>
                    </div>

                    <IoMdClose
                      className="text-xl text-white cursor-pointer"
                      onClick={() => setIsTradeModalOpen(false)}
                    />
                  </div>

                  <hr className="border-white/10" />

                  <form
                    onSubmit={formik.handleSubmit}
                    className="max-w-md mx-auto mt-8"
                  >
                    <div className="flex flex-col gap-2 mt-4">
                      <label htmlFor="type" className="text-sm text-white/70">
                        Type
                      </label>
                      <div className="relative flex items-center justify-between transition-all duration-200 w-full overflow-hidden bg-gray-600 border border-primary/40 rounded-lg text-sm text-white/70 placeholder-white/40 focus:outline-none focus:border-primary opacity-60">
                        <select
                          name="type"
                          id="type"
                          value="sell"
                          disabled
                          className="bg-gray-600 px-4 py-3 appearance-none focus:outline-none w-full cursor-not-allowed"
                        >
                          <option value="sell">Sell</option>
                        </select>
                        <FiChevronDown className="absolute top-4 right-3 text-white/60 text-base z-10" />
                      </div>
                    </div>
                    <div className="flex flex-col gap-2 mt-4">
                      <label htmlFor="card" className="text-sm text-white/70">
                        Select card
                      </label>
                      <CardCombobox
                        id="card"
                        cards={cards.filter(
                          (card) => card.type === formik.values.type && !card.isDisabled
                        )}
                        value={formik.values.card}
                        onChange={(card) => {
                          setCurrentRate(Number(card.rate));
                          // Mark touched WITHOUT validating, then set the value WITH
                          // validation, so the check sees the new card.
                          formik.setFieldTouched("card", true, false);
                          formik.setFieldValue("card", card._id, true);
                        }}
                        onBlur={() => formik.setFieldTouched("card", true, true)}
                      />
                      <p className="text-red-500 text-xs">
                        {formik.touched.card &&
                          Boolean(formik.errors.card) &&
                          formik.errors.card}
                      </p>
                    </div>

                    <div className="flex flex-col gap-2 mt-4">
                      <label htmlFor="amount" className="text-sm text-white/70">
                        Total amount
                      </label>
                      <input
                        id="amount"
                        name="amount"
                        type="number"
                        step="0.01"
                        min="0.0"
                        value={formik.values.amount}
                        onChange={formik.handleChange}
                        onWheel={(event) => event.currentTarget.blur()}
                        placeholder="0.0"
                        className="form-input spin-button-none"
                      />

                      <p className="text-red-500 text-xs">
                        {formik.touched.amount &&
                          Boolean(formik.errors.amount) &&
                          formik.errors.amount}
                      </p>
                    </div>

                    <div className="flex flex-col gap-2 mt-4">
                      <label
                        htmlFor="extrainfo"
                        className="text-xs text-white/70"
                      >
                        Extra information (optional)
                      </label>
                      <textarea
                        name="extrainfo"
                        id="extrainfo"
                        cols={10}
                        rows={5}
                        onChange={formik.handleChange}
                        value={formik.values.extrainfo}
                        className="form-input spin-button-none"
                        placeholder="Input Ecode if available"
                      />

                      <p className="text-red-500 text-xs">
                        {formik.touched.extrainfo &&
                          Boolean(formik.errors.extrainfo) &&
                          formik.errors.extrainfo}
                      </p>
                    </div>

                    <div className="mt-4">
                      <div className="flex items-center gap-3">
                        <label
                          htmlFor="file"
                          className={`transition-all duration-200 text-sm inline-flex items-center gap-2 px-4 py-2 rounded-xl ${
                            files.length >= MAX_FILES
                              ? "bg-gray-600 text-white/30 cursor-not-allowed"
                              : "bg-gray-500 hover:bg-gray-500/80 text-white/60 cursor-pointer"
                          }`}
                        >
                          <IoMdAttach className={`text-lg ${files.length >= MAX_FILES ? "text-white/30" : "text-white/70"}`} /> 
                          Attach file(s)
                        </label>
                        {files.length > 0 && (
                          <span className="text-xs text-white/50">
                            {files.length}/{MAX_FILES} files
                          </span>
                        )}
                      </div>
                      <input
                        type="file"
                        name="file"
                        id="file"
                        multiple
                        accept="image/*"
                        onChange={handleFileInput}
                        className="sr-only"
                        disabled={files.length >= MAX_FILES}
                      />

                      <p className="text-red-500 text-xs">
                        {formik.touched.file &&
                          Boolean(formik.errors.file) &&
                          formik.errors.file}
                      </p>

                      <div className="flex flex-col gap-2">
                        {
                          // Show file name if file is selected

                          files?.map((val, index) => (
                            <div key={index} className="flex items-center gap-2 mt-3">
                              <p className="px-3 py-2 rounded-md text-sm text-white/70 bg-green-500/20 border-2 border-green-400 flex items-center flex-wrap gap-2">
                                {val.name}{" "}
                                <span className="bg-white/30 text-xs font-normal px-2 py-1 inline rounded-md">
                                  {formatFileSize(val.size)}
                                </span>
                              </p>
                              <button
                                type="button"
                                className="p-1 text-sm text-red-600"
                                onClick={() => handleResetFile(val.name)}
                              >
                                Remove
                              </button>
                            </div>
                          ))
                        }
                      </div>
                    </div>

                    {formik.values.amount > 0.0 && (
                      <div className="w-full bg-gray-700 rounded-md border-2 border-dashed border-white/10 px-4 py-4 mt-4 flex flex-col items-start gap-3">
                        <div className="flex justify-between items-center gap-6 flex-wrap w-full">
                          {/* <div className="flex flex-col">
                            <p className="text-xs text-white/40">
                              Amount in USD(with VAT)
                            </p>
                            <p className="text-white text-base font-bold">
                              {formik.values.amount}
                            </p>
                          </div> */}
                          <div className="flex flex-col">
                            <p className="text-xs text-white/40 flex items-center gap-1">
                              You Will Receive
                              <IoMdInformationCircleOutline
                                className="text-primary cursor-pointer text-base"
                                onClick={() => setIsCurrencyInfoOpen(true)}
                              />
                            </p>
                            <p className="text-green-400 text-base font-bold">
                              {Number(estimate || 0).toFixed(2)}{" "}
                              {
                                currenciesSelect.find(
                                  (cur) => cur.value === currency
                                )?.symbol
                              }
                            </p>
                          </div>
                          <div>
                            {/* Toggle Currency of ngn, usd and ghs */}
                            <select
                              name="currency"
                              id="currency-select"
                              className="px-2 py-1 rounded-lg bg-primary outline-none"
                              onChange={(e) => setCurrency(e.target.value)}
                            >
                              {currenciesSelect.map((cur) => (
                                <option
                                  value={cur.value}
                                  selected={currency === cur.value}
                                >
                                  {cur.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="flex items-center gap-2 mt-4">
                      <input
                        type="checkbox"
                        name="acceptTerms"
                        id="acceptTerms"
                        className="form-checkbox"
                        checked={formik.values.acceptTerms}
                        onChange={formik.handleChange}
                      />
                      <label
                        htmlFor="acceptTerms"
                        className="text-sm text-white/70"
                      >
                        I agree to the{" "}
                        <a
                          target="_blank"
                          href="https://gcbuying.com/terms"
                          className="text-white/100 underline hover:text-white/60 transition-all duration-200"
                        >
                          Terms and Conditions{" "}
                        </a>
                        &{" "}
                        <a
                          target="_blank"
                          href="https://gcbuying.com/privacy"
                          className="text-white/100 underline hover:text-white/60 transition-all duration-200"
                        >
                          Privacy Policy
                        </a>
                      </label>
                    </div>
                    <p className="text-red-500 text-xs">
                      {formik.touched.acceptTerms &&
                        Boolean(formik.errors.acceptTerms) &&
                        formik.errors.acceptTerms}
                    </p>

                    <button
                      type="submit"
                      className="btn-primary px-4 py-2 rounded-md mt-6"
                    >
                      Sell Gift Card
                    </button>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {isCurrencyInfoOpen && createPortal(
        <div className="fixed inset-0 z-[9999]" role="dialog" aria-modal="true">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setIsCurrencyInfoOpen(false)}
          />
          {/* Scrollable overlay — this outer div IS the scroll container */}
          <div className="fixed inset-0 overflow-y-auto z-[10000]">
            <div className="flex min-h-full items-center justify-center p-4">
              {/* Panel — no max-h, content flows naturally, outer div scrolls */}
              <div className="relative font-dashboard w-full max-w-2xl rounded-2xl bg-[#161819] px-6 pt-4 pb-6 text-left shadow-xl">
                {/* Sticky header */}
                <div className="flex items-center justify-between pb-4">
                  <p className="text-base text-white font-medium">
                    Understanding Currency Options & Payout Methods
                  </p>
                  <IoMdClose
                    className="text-xl text-white cursor-pointer"
                    onClick={() => setIsCurrencyInfoOpen(false)}
                  />
                </div>
                <hr className="border-white/10" />

                {/* Content — flows naturally, outer div scrolls */}
                <div className="mt-4">
                  <p className="text-sm text-white/60 mb-4">
                    The currency toggle allows you to select the currency in which you would like to receive the proceeds from your gift card sale. The estimated value you will receive is displayed before you submit your sell order, and the toggle can be used to switch between the available currencies.
                  </p>
                  <p className="text-sm text-white/60 mb-4">
                    Below is a breakdown of the supported payout options for each currency.
                  </p>

                  <h3 className="text-white font-semibold text-base mt-4 mb-2">NGN (Nigerian Naira)</h3>
                  <p className="text-sm text-white/60 mb-1">Users who select NGN will receive the proceeds of their gift card sale in Nigerian Naira.</p>
                  <ul className="text-sm text-white/60 list-disc list-inside mb-4 space-y-1">
                    <li>Funds are credited to the user's NGN wallet balance within the platform.</li>
                    <li>Users can withdraw directly to their Nigerian bank account.</li>
                    <li>Bank withdrawals are fully automated and typically processed instantly.</li>
                  </ul>

                  <h3 className="text-white font-semibold text-base mt-4 mb-2">USD (United States Dollar)</h3>
                  <p className="text-sm text-white/60 mb-1">Users who select USD will receive their payout through cryptocurrency-based withdrawal options.</p>
                  <p className="text-sm text-white/60 mb-1">Supported payout methods include:</p>
                  <ul className="text-sm text-white/60 list-disc list-inside mb-3 space-y-1">
                    <li>Bitcoin (BTC)</li>
                    <li>USDT (Tether – ERC20)</li>
                    <li>USDT (Tether – TRC20)</li>
                    <li>Litecoin (LTC)</li>
                    <li>Dogecoin (DOGE)</li>
                    <li>Ethereum (ETH)</li>
                    <li>Cash App Bitcoin</li>
                  </ul>
                  <p className="text-sm text-white/60 mb-1">Users can obtain a Bitcoin or crypto wallet address from many widely used service providers, including but not limited to:</p>
                  <ul className="text-sm text-white/60 list-disc list-inside mb-3 space-y-1">
                    <li>Cash App</li>
                    <li>Coinbase</li>
                    <li>PayPal</li>
                    <li>BitPay</li>
                    <li>Strike</li>
                    <li>Other supported cryptocurrency wallet providers</li>
                  </ul>
                  <p className="text-sm text-white/60 mb-4">Once the withdrawal is processed, funds will be sent directly to the wallet address provided by the user.</p>

                  <h3 className="text-white font-semibold text-base mt-4 mb-2">GHS (Ghanaian Cedi)</h3>
                  <p className="text-sm text-white/60 mb-1">Users who select GHS will receive their payout in Ghanaian Cedis.</p>
                  <ul className="text-sm text-white/60 list-disc list-inside mb-4 space-y-1">
                    <li>Withdrawals are processed via Mobile Money (MoMo).</li>
                    <li>Payments are sent directly to the mobile number linked to the user's mobile money wallet.</li>
                  </ul>

                  <h3 className="text-white font-semibold text-base mt-4 mb-2">Additional Note</h3>
                  <p className="text-sm text-white/60 mb-1">The estimated value you will receive from a gift card sale is displayed before submitting the order, allowing you to review the payout amount in your preferred currency.</p>
                  <p className="text-sm text-white/60 mb-4">You can use the currency toggle to switch between NGN, USD, or GHS and instantly see the corresponding payout value before completing the transaction.</p>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};
